import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useTimer from 'src/common/hooks/useTimer/useTimer';
import useLocalStorageState from 'src/common/hooks/useLocalStorageState';
import { LayoutTimerProps } from './LayoutTimer.types';
import { LocalStorageKeys } from 'src/types/common';
import UITooltip from '../common/ui/UITooltip/UITooltip';
import * as Styles from './LayoutTimer.styles';
import { FlexContainer } from 'src/components/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getRemainingMs } from 'src/utils/dates/getRemainingMs';

const AUTOCLOSE_MS = 5750;

const LayoutTimer = ({
  initialTime,
  interval,
  enabled = false,
  onFinish,
  storageKey,
  size = 'medium',
  icon,
  fabProps,
}: LayoutTimerProps) => {
  const [storedTime] = useLocalStorageState(
    storageKey ? storageKey : LocalStorageKeys.CartExpirationTime,
    initialTime || 0
  );
  const [forcedOpen, setForcedOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openTemporarily = useCallback(() => {
    setForcedOpen(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setForcedOpen(false), AUTOCLOSE_MS);
  }, []);

  const { formattedTimer, time } = useTimer({
    initialTime: getRemainingMs(storedTime as number),
    interval,
    enabled,
    onFinish,
  });

  useEffect(() => {
    if (!enabled) return;
    if (typeof time === 'number' && time <= 0) openTemporarily();
  }, [time, enabled, openTemporarily]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const iconSizes = { small: 20, medium: 24, large: 32 };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: (theme) => theme.zIndex.fab,
      }}
    >
      <Styles.FreeShippingBadge>ENVÍO GRATIS</Styles.FreeShippingBadge>
      <UITooltip
        color="success"
        open={forcedOpen}
        arrow
        title={
          <Typography
            variant="body2"
            color="inherit"
            fontWeight="fontWeightMedium"
          >
            {time && time > 0
              ? '¡Envío gratis por tiempo limitado! Finaliza tu compra ahora y aprovéchalo.'
              : 'El envío gratis ha expirado, pero aún puedes completar tu pedido con envío estándar.'}
          </Typography>
        }
      >
        <Styles.TimerFab
          {...fabProps}
          sx={{ ...fabProps?.sx }}
          color="primary"
          variant="extended"
          onClick={openTemporarily}
        >
          <FlexContainer
            gap={1}
            alignItems="center"
            overflow="hidden"
            flexWrap="nowrap"
          >
            <>
              {icon ?? <AccessTimeIcon sx={{ fontSize: iconSizes[size] }} />}
              <Typography
                variant="body2"
                color="inherit"
                sx={{ whiteSpace: 'nowrap' }}
                fontWeight="fontWeightMedium"
              >
                {formattedTimer}
              </Typography>
            </>
          </FlexContainer>
        </Styles.TimerFab>
      </UITooltip>
    </Box>
  );
};

export default LayoutTimer;
